/**
 * v1 Create Allowed Domain Dialog Component
 * Modal for creating new domain policies
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { v1AllowedDomainsApi } from '../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { TagInput } from '@/components/ui/tag-input'
import { Shield, AlertCircle } from 'lucide-react'

interface CreateAllowedDomainDialogProps {
  open: boolean
  onClose: () => void
}

export function CreateAllowedDomainDialog({ open, onClose }: CreateAllowedDomainDialogProps) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '',
    domains: [] as string[],
  })
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: (data: any) => v1AllowedDomainsApi.createAllowedDomain(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowedDomains'] })
      toast.success('Policy created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Failed to create policy')
    },
  })

  const handleClose = () => {
    setForm({ name: '', domains: [] })
    setError('')
    onClose()
  }

  const handleSubmit = () => {
    setError('')

    if (!form.name.trim()) {
      setError('Policy name is required')
      return
    }

    if (form.domains.length === 0) {
      setError('At least one domain is required')
      return
    }

    createMutation.mutate({
      name: form.name,
      allowedDomains: form.domains,
      isActive: true,
    })
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Create Domain Policy
          </DialogTitle>
          <DialogDescription>
            Define which email domains are allowed for SaifChat user registration
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Policy Name</Label>
            <Input
              id="name"
              placeholder="e.g., Company Employees, Partner Organizations"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="domains">Allowed Domains</Label>
            <TagInput
              value={form.domains}
              onChange={(domains) => setForm({ ...form, domains })}
              placeholder="Enter domain and press space or enter..."
              disabled={createMutation.isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}